import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { initializeApp } from 'firebase/app';
import { getAuth, GoogleAuthProvider, signInWithPopup, signOut, User } from 'firebase/auth';
import { getAnalytics } from 'firebase/analytics';
import {HttpClient, HttpContext, HttpHeaders} from "@angular/common/http";
import {SKIP_AUTH} from "../theme/utils/auth-context";
import {ApiUrl} from "../shared/models/apiurl.models";
import {UserStorageService} from "./UserStorageService";
declare const google: any;

export interface GoogleUser {
    id: string;
    name: string;
    email: string;
    picture?: string;
    id_token: string;
}

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    // withCredentials: defense-in-depth alongside the AppInterceptor fix - see
    // theme/utils/app-interceptor.ts for the full incident writeup. This is
    // the Google/social login call, which also sets the refresh cookie.
    private options = {
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': this.apiKey.getJWTToken()
        }),
        withCredentials: true
    }
    private userSubject = new BehaviorSubject<GoogleUser | null>(null);
    user$: Observable<GoogleUser | null> = this.userSubject.asObservable();

    private clientId = 'YOUR_GOOGLE_CLIENT_ID.apps.googleusercontent.com';
    private buttonRendered = false;

    constructor(private ngZone: NgZone, private http: HttpClient, private apiKey: ApiUrl, private userStorageService: UserStorageService) {
        this.auth.onAuthStateChanged((user:User) => {
            // this.userSubject.next(user);
        });
    }

    initGoogleSignIn(buttonId = 'google-signin-button') {
        // Wait for google object
        if (typeof google === 'undefined') {
            console.error('Google Identity Services library not loaded.');
            return;
        }

        // initialize the client
        google.accounts.id.initialize({
            client_id: this.clientId,
            callback: (response: any) => this.handleCredentialResponse(response),
            // optional: ux_mode: 'popup' // or 'redirect' depending on desired behavior
        });

        // render a Google button (only once)
        if (!this.buttonRendered) {
            google.accounts.id.renderButton(
                document.getElementById(buttonId),
                { theme: 'outline', size: 'large' } // customization
            );
            this.buttonRendered = true;
        }

        // optionally automatically prompt the One Tap (comment out if not wanted)
        // google.accounts.id.prompt();
    }

    private handleCredentialResponse(response: any) {
        // response.credential is a JWT ID token (base64url)
        if (!response || !response.credential) {
            console.error('No credential returned from Google');
            return;
        }

        // parse token payload locally (not a substitute for backend verification)
        const payload = this.parseJwt(response.credential);
        const user: GoogleUser = {
            id: payload.sub,
            name: payload.name,
            email: payload.email,
            picture: payload.picture,
            id_token: response.credential
        };

        // make sure to run inside Angular zone because callback is external
        this.ngZone.run(() => this.userSubject.next(user));

        // TODO: send `user.id_token` to your backend to verify and create / sign-in user server-side
    }

    signOut() {
        // Clear local app state
        this.userSubject.next(null);
        // Revoke token or disable auto select if needed:
        // google.accounts.id.revoke(email, done => { ... })
        // Also remove prompt if needed:
        // google.accounts.id.disableAutoSelect();
    }

    private parseJwt(token: string): any {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error('Failed to parse JWT', e);
            return {};
        }
    }


    private firebaseConfig = {
        apiKey: "AIzaSyDdbEETB8FTQIhkjInyLzJ06_N_cwgWr3s",
        authDomain: "babyapp-54866.firebaseapp.com",
        projectId: "babyapp-54866",
        storageBucket: "babyapp-54866.firebasestorage.app",
        messagingSenderId: "491563698818",
        appId: "1:491563698818:web:362d7db936207b4269829b",
        measurementId: "G-2Q17D1M11K"
    };

    private app = initializeApp(this.firebaseConfig);
    private analytics = getAnalytics(this.app);
    private auth = getAuth(this.app);
    private provider = new GoogleAuthProvider();





    async signInWithGoogle() {
        try {
            const result = await signInWithPopup(this.auth, this.provider);
            const user = result.user;

            // POD1-10/POD1-17: the backend no longer trusts `uid`/`email` from
            // the body — it verifies this signed token with Firebase and takes
            // every identity field from the VERIFIED claims. Without `idToken`
            // in the payload, api-token-auth/ answers 401 invalid_credentials.
            // The token was already being fetched here and thrown away.
            const idToken = await user.getIdToken();

            const displayName = user.displayName ?? '';
            const nameParts = displayName.split(' ').filter(Boolean);

            var registerForm =
                {
                    idToken: idToken,
                    username: `${user.email}`,
                    email: `${user.email}`,
                    phoneNumber: user.phoneNumber ?? null,
                    page_source: 'GOOGLE_SIGNIN',
                    acceptAdv: true,
                    first_name: nameParts[0] ?? '',
                    last_name: nameParts.slice(1).join(' '),
                    acceptTerms: true,
                    uid: user!.uid
                }
            // ONE login call (2026-10-24): it used to be posted twice, which
            // opened two server sessions per sign-in. No Authorization header
            // and SKIP_AUTH - a login never carries the previous session.
            const authenticatedLogin = await firstValueFrom(
                this.http.post<any>(this.apiKey.loginUser(), JSON.stringify(registerForm), {
                    headers: new HttpHeaders({'Content-Type': 'application/json'}),
                    withCredentials: true,
                    context: new HttpContext().set(SKIP_AUTH, true),
                })
            );
            if (authenticatedLogin['status'] != 200) {
                throw new Error('Sale4All Google login was not accepted');
            }
            this.userStorageService.saveUser(authenticatedLogin['user']);
            // this.userSubject.next(user);
            // console.log('✅ Google sign-in successful:', user);
            return authenticatedLogin;
        } catch (error) {
            console.error('❌ Google sign-in failed:', error);
            throw error;
        }
    }

    async logout() {
        await signOut(this.auth);
        this.userSubject.next(null);
        console.log('👋 User signed out');
    }
}
